import { type Node, type NodeProps, Position } from '@xyflow/react';
import React from 'react';

import { createHandleId } from '../../utils/handle-ids';
import { BaseNode, type NodeData } from './BaseNode';
import { NodeHandle } from './NodeHandle';

export type SemanticFilterNodeType = Node<NodeData, 'custom_semantic_filter'>;

export const SemanticFilterNode: React.FC<
  NodeProps<SemanticFilterNodeType>
> = ({ id, data, selected }) => {
  const topK = data.config?.top_k as number;
  const similarityThreshold = data.config?.similarity_threshold as number;

  let subtitle: string | undefined;
  if (topK !== undefined) {
    subtitle = `Top ${topK}`;
    if (similarityThreshold !== undefined && similarityThreshold > 0) {
      subtitle += ` (threshold: ${similarityThreshold})`;
    }
  }

  // Extract connection state from node data for visual indicators
  const activeConnection = data.activeConnection;
  const isValidTargetHandle = data.isValidTargetHandle as
    | ((targetHandle: string) => boolean)
    | undefined;
  const isActiveConnection = !!activeConnection;

  return (
    <BaseNode
      nodeId={id}
      data={data}
      category="processing"
      subtitle={subtitle}
      selected={selected}
    >
      {/* Input handles with connection indicators */}
      <NodeHandle
        type="target"
        position={Position.Left}
        id="query"
        blockId={id}
        style={{ top: '30%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(createHandleId(id, 'query'))
            : undefined
        }
      />
      <NodeHandle
        type="target"
        position={Position.Left}
        id="candidates"
        blockId={id}
        style={{ top: '70%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(createHandleId(id, 'candidates'))
            : undefined
        }
      />
      {/* Output handle */}
      <NodeHandle
        type="source"
        position={Position.Right}
        id="filtered_results"
        blockId={id}
        isActiveConnection={isActiveConnection}
      />
    </BaseNode>
  );
};
